import { type PlayerHudEntry, PowerScoreBar } from '@aod/ui';
import type { Diplomacy } from '../../dynasty/diplomacy.js';
import { pointsOf } from '../../dynasty/diplomacy.js';
import { type Dynasty, standingWith } from '../../dynasty/dynasty.js';
import { useT } from '../../i18n/index.js';
import type { TreatyKind } from '../../sim/useSimWorld/useTreatyIntents.js';
import { RoyalCourt } from './RoyalCourt.js';
import { TreatyRow } from './TreatyRow.js';

interface DiplomacyTabProps {
  readonly players: readonly PlayerHudEntry[];
  readonly playerColors: ReadonlyMap<number, string>;
  /** The royal families, while `diplomacy.dynasty` is on. */
  readonly dynasty?: Dynasty | null;
  readonly localPlayerIndex?: number;
  readonly onProposeMarriage?: (toPlayer: number) => void;
  /** Who stands where, and the offers on the table, while `diplomacy.basic` is on. */
  readonly diplomacy?: Diplomacy | null;
  readonly onProposeTreaty?: (toPlayer: number, treaty: TreatyKind) => void;
  readonly onAnswerTreaty?: (fromPlayer: number, accept: boolean) => void;
  readonly onDeclareWar?: (toPlayer: number) => void;
}

export function DiplomacyTab({
  players,
  playerColors,
  dynasty,
  localPlayerIndex = 0,
  onProposeMarriage,
  diplomacy,
  onProposeTreaty,
  onAnswerTreaty,
  onDeclareWar,
}: DiplomacyTabProps) {
  const t = useT();
  if (players.length <= 1) {
    return <p className="text-[10px] text-muted-foreground">{t('diplomacy.empty')}</p>;
  }

  const sortedPlayers = [...players].sort((a, b) => b.powerScore - a.powerScore);

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {t('diplomacy.knownPlayers', { count: sortedPlayers.length })}
      </h3>
      <div className="flex flex-col gap-1.5">
        {sortedPlayers.map((player) => (
          <div
            key={player.playerId}
            className="flex flex-col gap-1.5 rounded-md border border-border/50 bg-card/60 px-2.5 py-2"
          >
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="size-3 shrink-0 rounded-full border border-border/60"
                style={{ backgroundColor: playerColors.get(player.playerId) ?? '#888' }}
              />
              <span className="min-w-0 flex-1 truncate whitespace-nowrap text-sm font-medium text-foreground">
                {player.name}
              </span>
              <PowerScoreBar score={player.powerScore} className="w-16 shrink-0 gap-1" />
              {!diplomacy && (
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {t('diplomacy.neutral')}
                </span>
              )}
            </div>
            {diplomacy && player.playerId !== localPlayerIndex && (
              <TreatyRow
                player={player}
                diplomacy={diplomacy}
                localPlayerIndex={localPlayerIndex}
                onPropose={onProposeTreaty}
                onAnswer={onAnswerTreaty}
                onDeclareWar={onDeclareWar}
                married={
                  dynasty
                    ? standingWith(dynasty, localPlayerIndex, player.playerId) === 'bonded'
                    : false
                }
              />
            )}
          </div>
        ))}
      </div>
      {diplomacy && (
        <p className="text-[10px] text-muted-foreground">
          {t('treaty.points', {
            points: pointsOf(diplomacy, localPlayerIndex),
            max: diplomacy.points.find((p) => p.seat === localPlayerIndex)?.max ?? 0,
            cost: diplomacy.actionCost,
          })}
        </p>
      )}
      {dynasty && (
        <RoyalCourt
          dynasty={dynasty}
          localPlayerIndex={localPlayerIndex}
          players={players}
          onPropose={onProposeMarriage}
        />
      )}
    </div>
  );
}
